import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart3,
  Building2,
  Users,
  ClipboardList,
  CheckCircle2,
  AlertTriangle,
  Target,
  FileText,
  ShieldCheck,
  Loader,
  Repeat,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import {
  analyticsSummaryResponse,
  analyticsCompaniesResponse,
  analyticsEmployeesResponse,
  platformTenantsResponse,
} from '../../lib/types'
import { useAuthStore } from '../../stores/authStore'
import { getCalendarTimeZone, formatDateOnly, formatNumber } from '../../lib/format'
import { analyticsPeriodRange } from '../../lib/analytics-range'
import { DataTable } from '../../components/table/DataTable'
import { DonutChart, Legend } from '../../components/charts/Charts'
import { StatusPill } from '../../components/ui/Pills'
import { StatusBadge } from '../../components/ui/StatusBadge'
import { usePageSize } from '../../lib/display-prefs'

// ── KPI card (matches the platform dashboard / Companies-page tile) ─────────
const KPI_TONES = {
  violet: {
    tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
  },
  emerald: {
    tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
  },
  sky: {
    tile: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
    halo: 'from-[#DBEAFE] to-transparent dark:from-transparent',
  },
  amber: {
    tile: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-white/[0.06] dark:text-orange-300 dark:border-white/10',
    halo: 'from-[#FFEDD5] to-transparent dark:from-transparent',
  },
  rose: {
    tile: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10',
    halo: 'from-[#FFE4E6] to-transparent dark:from-transparent',
  },
  slate: {
    tile: 'bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0] dark:bg-white/[0.06] dark:text-slate-300 dark:border-white/10',
    halo: 'from-[#F1F5F9] to-transparent dark:from-transparent',
  },
}

function KpiCard({ label, value, icon: Icon, tone = 'violet', hint }) {
  const tones = KPI_TONES[tone] ?? KPI_TONES.violet
  return (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 transition-all duration-200 hover:border-[color:var(--color-accent-border)] hover:shadow-lg hover:-translate-y-0.5">
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0">
            {label}
          </h3>
          <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
            {value == null ? '—' : formatNumber(Number(value))}
          </p>
          {hint ? (
            <p className="mt-2 text-[13px] text-[color:var(--color-ink-muted)]">{hint}</p>
          ) : null}
        </div>
        <div className={'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' + tones.tile}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}

// Fallback palette (To Do / In Progress / Complete / Overdue) mirroring the
// Space workflow colours. The API sends an explicit `color` per segment; this
// only applies if that is ever missing.
const DONUT_COLORS = ['#94a3b8', '#a855f7', '#10b981', '#ef4444']

// Fetch every row under the current filters for a client-side export, capped so
// a huge platform export can't hang the browser. Mirrors TenantsPage's pattern.
async function fetchAllPages(url, params, parse, cap = 5000) {
  const out = []
  let page = 1
  const limit = 200
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await api.get(url, { params: { ...params, page, limit } })
    const parsed = parse(res.data)
    out.push(...parsed.data)
    const totalPages = parsed.pagination?.totalPages ?? 1
    if (page >= totalPages || out.length >= cap) break
    page += 1
  }
  return out.slice(0, cap)
}

export default function AnalyticsPage() {
  const user = useAuthStore((s) => s.user)
  const isPlatform = user?.role === 'super_admin'
  const base = isPlatform ? '/super-admin/analytics' : '/analytics'

  const [company, setCompany] = useState('') // platform drill-down (tenantId)
  const [department, setDepartment] = useState('') // per-company department filter
  const [period, setPeriod] = useState('all') // all | week | month | custom
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  // [QA-15] The period the data is actually scoped to. The controls above are
  // what the user is editing; a custom range is applied only once both dates
  // are set and in order — until then the previous period stays applied (and
  // labelled) instead of silently becoming all-time "selected range".
  const [applied, setApplied] = useState({ period: 'all', customFrom: '', customTo: '' })
  const [empSearch, setEmpSearch] = useState('')
  const [empPage, setEmpPage] = useState(1)
  // Both lists start at (and follow) the account rows-per-page, capped at
  // 200 — the analytics API rejects a larger page (progress.dto.js).
  const [empLimit, setEmpLimit, analyticsLimitOptions] = usePageSize({ max: 200 })
  const [coSearch, setCoSearch] = useState('')
  const [coPage, setCoPage] = useState(1)
  const [coLimit, setCoLimit] = usePageSize({ max: 200 })

  // Company dropdown source (platform only) — reuse the tenants list.
  const companiesFilterQuery = useQuery({
    queryKey: ['analytics-company-filter'],
    queryFn: async () => {
      const res = await api.get('/super-admin/tenants', { params: { limit: 100 } })
      return platformTenantsResponse.parse(res.data)
    },
    enabled: isPlatform,
    staleTime: 5 * 60_000,
  })
  const companyOptions = useMemo(
    () =>
      (companiesFilterQuery.data?.data ?? [])
        .map((t) => ({ value: t.id, label: t.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [companiesFilterQuery.data],
  )

  // [QA-15] The calendar the period's day edges resolve in: the company's own
  // timezone (the drilled company on the platform view, the signed-in tenant
  // otherwise); on the all-companies view the one zone they share; the
  // browser's only when nothing better is known.
  const rangeTz = useMemo(() => {
    // Range edges are calendar days — the company's, not the viewer's own zone.
    if (!isPlatform) return getCalendarTimeZone()
    const rows = companiesFilterQuery.data?.data ?? []
    if (company) return rows.find((t) => t.id === company)?.timezone || undefined
    const zones = new Set(rows.map((t) => t.timezone).filter(Boolean))
    return zones.size === 1 ? [...zones][0] : undefined
  }, [isPlatform, company, companiesFilterQuery.data])

  // What the controls currently say (drives the inline prompt) …
  const draftRange = analyticsPeriodRange(period, { customFrom, customTo }, rangeTz)
  // … and the applied period → { from, to } ISO window (empty = all-time).
  // Scopes the "flow" metrics (Completed, Reports submitted); stock metrics
  // stay current-state. `applied` is only ever set to a valid period.
  const range = useMemo(
    () => analyticsPeriodRange(applied.period, applied, rangeTz).range ?? {},
    [applied, rangeTz],
  )
  function applyPeriodIfValid(next) {
    if (analyticsPeriodRange(next.period, next, rangeTz).status === 'ok') {
      setApplied(next)
      setEmpPage(1)
      setCoPage(1)
    }
  }

  // "Today" is a task-SET scope (created or running today) rather than a
  // completion window, so it sends `today=1` instead of from/to.
  const windowParams = useMemo(
    () => (applied.period === 'today' ? { today: true } : range),
    [applied.period, range],
  )

  const summaryParams = useMemo(
    () => ({
      ...(company ? { tenantId: company } : {}),
      ...(department ? { departmentId: department } : {}),
      ...windowParams,
    }),
    [company, department, windowParams],
  )

  const summaryQuery = useQuery({
    queryKey: ['analytics-summary', base, company, department, applied.period, range.from, range.to],
    queryFn: async () => {
      const res = await api.get(`${base}/summary`, { params: summaryParams })
      return analyticsSummaryResponse.parse(res.data)
    },
    placeholderData: (prev) => prev,
  })

  // The comparison table honours the same filters as the KPIs: a selected
  // company scopes it to that one row, and its own search box filters by name.
  const companyParams = useMemo(
    () => ({
      ...(company ? { tenantId: company } : {}),
      ...(coSearch ? { search: coSearch } : {}),
      ...windowParams,
      page: coPage,
      limit: coLimit,
    }),
    [company, coSearch, coPage, coLimit, windowParams],
  )
  const companiesQuery = useQuery({
    queryKey: ['analytics-companies', company, coSearch, coPage, coLimit, applied.period, range.from, range.to],
    queryFn: async () => {
      const res = await api.get('/super-admin/analytics/companies', {
        params: companyParams,
      })
      return analyticsCompaniesResponse.parse(res.data)
    },
    enabled: isPlatform,
    placeholderData: (prev) => prev,
  })

  const employeeParams = useMemo(
    () => ({
      ...(company ? { tenantId: company } : {}),
      ...(department ? { departmentId: department } : {}),
      ...(empSearch ? { search: empSearch } : {}),
      ...windowParams,
      page: empPage,
      limit: empLimit,
    }),
    [company, department, empSearch, empPage, empLimit, windowParams],
  )
  const employeesQuery = useQuery({
    queryKey: ['analytics-employees', base, company, department, empSearch, empPage, empLimit, applied.period, range.from, range.to],
    queryFn: async () => {
      const res = await api.get(`${base}/employees`, { params: employeeParams })
      return analyticsEmployeesResponse.parse(res.data)
    },
    placeholderData: (prev) => prev,
  })

  // Labels the APPLIED period — never 'selected range' for a range that isn't.
  const ap = applied.period
  const periodLabel =
    ap === 'today' ? 'today' : ap === 'week' ? 'this week' : ap === 'month' ? 'this month' : ap === 'custom' ? 'selected range' : 'all time'
  const departments = summaryQuery.data?.departments ?? []
  const kpis = summaryQuery.data?.kpis ?? {}
  const taskStatus = summaryQuery.data?.breakdowns?.taskStatus ?? []
  const donutSegments = taskStatus.map((s, i) => ({
    label: s.label,
    value: s.value,
    color: s.color || DONUT_COLORS[i % DONUT_COLORS.length],
  }))
  const donutTotal = donutSegments.reduce((a, s) => a + (s.value ?? 0), 0)

  const companyColumns = useMemo(
    () => [
      { key: 'company', header: 'Company', grow: true, pinnable: true, minWidth: 180, sortAccessor: (r) => (r.company ?? '').toLowerCase(), exportValue: (r) => r.company ?? '', render: (r) => <span className="font-semibold text-[color:var(--color-ink-strong)]">{r.company}</span> },
      { key: 'status', header: 'Status', minWidth: 110, sortAccessor: (r) => r.status ?? '', exportValue: (r) => r.status ?? '', render: (r) => <StatusBadge status={r.status} /> },
      { key: 'planTier', header: 'Plan', minWidth: 100, defaultHidden: true, exportValue: (r) => r.planTier ?? '', render: (r) => r.planTier || '—' },
      { key: 'employees', header: 'Employees', align: 'right', minWidth: 100, sortAccessor: (r) => r.employees, exportValue: (r) => r.employees },
      { key: 'tasksTotal', header: 'Tasks', align: 'right', minWidth: 90, sortAccessor: (r) => r.tasksTotal, exportValue: (r) => r.tasksTotal },
      { key: 'tasksCompleted', header: 'Completed', align: 'right', minWidth: 110, sortAccessor: (r) => r.tasksCompleted, exportValue: (r) => r.tasksCompleted },
      { key: 'tasksOpen', header: 'Open', align: 'right', minWidth: 90, sortAccessor: (r) => r.tasksOpen, exportValue: (r) => r.tasksOpen },
      { key: 'tasksOverdue', header: 'Overdue', align: 'right', minWidth: 100, sortAccessor: (r) => r.tasksOverdue, exportValue: (r) => r.tasksOverdue, render: (r) => <span className={r.tasksOverdue > 0 ? 'text-[color:var(--color-danger)] font-semibold' : ''}>{r.tasksOverdue}</span> },
      // Normal (one-off) vs Repeated (recurring-occurrence) split of the same
      // Completed/Open/Overdue metrics. Normal + Repeated reconcile to the totals.
      { key: 'normalCompleted', header: 'Normal Completed', align: 'right', minWidth: 130, sortAccessor: (r) => r.normalCompleted, exportValue: (r) => r.normalCompleted },
      { key: 'normalOpen', header: 'Normal Open', align: 'right', minWidth: 110, sortAccessor: (r) => r.normalOpen, exportValue: (r) => r.normalOpen },
      { key: 'normalOverdue', header: 'Normal Overdue', align: 'right', minWidth: 130, sortAccessor: (r) => r.normalOverdue, exportValue: (r) => r.normalOverdue, render: (r) => <span className={r.normalOverdue > 0 ? 'text-[color:var(--color-danger)] font-semibold' : ''}>{r.normalOverdue}</span> },
      { key: 'repeatedTotal', header: 'Total Repeated', align: 'right', minWidth: 120, sortAccessor: (r) => r.repeatedTotal, exportValue: (r) => r.repeatedTotal },
      { key: 'repeatedCompleted', header: 'Repeated Completed', align: 'right', minWidth: 145, sortAccessor: (r) => r.repeatedCompleted, exportValue: (r) => r.repeatedCompleted },
      { key: 'repeatedOpen', header: 'Repeated Open', align: 'right', minWidth: 120, sortAccessor: (r) => r.repeatedOpen, exportValue: (r) => r.repeatedOpen },
      { key: 'repeatedOverdue', header: 'Repeated Overdue', align: 'right', minWidth: 140, sortAccessor: (r) => r.repeatedOverdue, exportValue: (r) => r.repeatedOverdue, render: (r) => <span className={r.repeatedOverdue > 0 ? 'text-[color:var(--color-danger)] font-semibold' : ''}>{r.repeatedOverdue}</span> },
      { key: 'completionRate', header: 'Completion', align: 'right', minWidth: 110, sortAccessor: (r) => r.completionRate, exportValue: (r) => `${r.completionRate}%`, render: (r) => `${r.completionRate}%` },
      { key: 'goalsCompleted', header: 'Goals done', align: 'right', minWidth: 110, defaultHidden: true, sortAccessor: (r) => r.goalsCompleted, exportValue: (r) => r.goalsCompleted },
      { key: 'reportsSubmitted', header: 'Reports', align: 'right', minWidth: 100, defaultHidden: true, sortAccessor: (r) => r.reportsSubmitted, exportValue: (r) => r.reportsSubmitted },
      { key: 'verificationPending', header: 'Verif. pending', align: 'right', minWidth: 120, defaultHidden: true, sortAccessor: (r) => r.verificationPending, exportValue: (r) => r.verificationPending },
    ],
    [],
  )

  const employeeColumns = useMemo(
    () => [
      { key: 'employee', header: 'Employee', grow: true, pinnable: true, minWidth: 180, sortAccessor: (r) => (r.employee ?? '').toLowerCase(), exportValue: (r) => r.employee ?? '', render: (r) => (<div className="min-w-0"><div className="font-semibold text-[color:var(--color-ink-strong)] truncate">{r.employee}</div><div className="text-xs text-[color:var(--color-ink-muted)] truncate">{r.email}</div></div>) },
      // Email is shown as the subtitle under the name on screen; expose it as
      // its own (hidden-by-default) column so it rides along in exports too.
      { key: 'email', header: 'Email', minWidth: 200, defaultHidden: true, sortAccessor: (r) => (r.email ?? '').toLowerCase(), exportValue: (r) => r.email ?? '', render: (r) => r.email || '—' },
      ...(isPlatform ? [{ key: 'company', header: 'Company', minWidth: 150, sortAccessor: (r) => (r.company ?? '').toLowerCase(), exportValue: (r) => r.company ?? '', render: (r) => r.company || '—' }] : []),
      { key: 'workRole', header: 'Work role', minWidth: 110, defaultHidden: true, exportValue: (r) => r.workRole ?? '', render: (r) => r.workRole || '—' },
      // "Assigned" — tasks assigned to this person (via TaskAssignee), which is a
      // different population from the company table's "Tasks" (all tasks in the
      // company); the two intentionally don't sum.
      { key: 'tasksTotal', header: 'Assigned', align: 'right', minWidth: 90, sortAccessor: (r) => r.tasksTotal, exportValue: (r) => r.tasksTotal },
      { key: 'tasksCompleted', header: 'Completed', align: 'right', minWidth: 110, sortAccessor: (r) => r.tasksCompleted, exportValue: (r) => r.tasksCompleted },
      { key: 'tasksOpen', header: 'Open', align: 'right', minWidth: 90, sortAccessor: (r) => r.tasksOpen, exportValue: (r) => r.tasksOpen },
      { key: 'tasksOverdue', header: 'Overdue', align: 'right', minWidth: 100, sortAccessor: (r) => r.tasksOverdue, exportValue: (r) => r.tasksOverdue, render: (r) => <span className={r.tasksOverdue > 0 ? 'text-[color:var(--color-danger)] font-semibold' : ''}>{r.tasksOverdue}</span> },
      // Normal (one-off) vs Repeated (recurring-occurrence) split of this person's
      // assigned Completed/Open/Overdue. Normal + Repeated reconcile to the totals.
      { key: 'normalCompleted', header: 'Normal Completed', align: 'right', minWidth: 130, sortAccessor: (r) => r.normalCompleted, exportValue: (r) => r.normalCompleted },
      { key: 'normalOpen', header: 'Normal Open', align: 'right', minWidth: 110, sortAccessor: (r) => r.normalOpen, exportValue: (r) => r.normalOpen },
      { key: 'normalOverdue', header: 'Normal Overdue', align: 'right', minWidth: 130, sortAccessor: (r) => r.normalOverdue, exportValue: (r) => r.normalOverdue, render: (r) => <span className={r.normalOverdue > 0 ? 'text-[color:var(--color-danger)] font-semibold' : ''}>{r.normalOverdue}</span> },
      { key: 'repeatedTotal', header: 'Total Repeated', align: 'right', minWidth: 120, sortAccessor: (r) => r.repeatedTotal, exportValue: (r) => r.repeatedTotal },
      { key: 'repeatedCompleted', header: 'Repeated Completed', align: 'right', minWidth: 145, sortAccessor: (r) => r.repeatedCompleted, exportValue: (r) => r.repeatedCompleted },
      { key: 'repeatedOpen', header: 'Repeated Open', align: 'right', minWidth: 120, sortAccessor: (r) => r.repeatedOpen, exportValue: (r) => r.repeatedOpen },
      { key: 'repeatedOverdue', header: 'Repeated Overdue', align: 'right', minWidth: 140, sortAccessor: (r) => r.repeatedOverdue, exportValue: (r) => r.repeatedOverdue, render: (r) => <span className={r.repeatedOverdue > 0 ? 'text-[color:var(--color-danger)] font-semibold' : ''}>{r.repeatedOverdue}</span> },
      { key: 'goalsCompleted', header: 'Goals done', align: 'right', minWidth: 110, defaultHidden: true, sortAccessor: (r) => r.goalsCompleted, exportValue: (r) => r.goalsCompleted },
      { key: 'reportsSubmitted', header: 'Reports', align: 'right', minWidth: 100, defaultHidden: true, sortAccessor: (r) => r.reportsSubmitted, exportValue: (r) => r.reportsSubmitted },
      { key: 'verificationPending', header: 'Verif. pending', align: 'right', minWidth: 120, defaultHidden: true, sortAccessor: (r) => r.verificationPending, exportValue: (r) => r.verificationPending },
      { key: 'status', header: 'Status', minWidth: 100, sortAccessor: (r) => r.status ?? '', exportValue: (r) => r.status ?? '', render: (r) => <StatusPill status={r.status} /> },
      { key: 'lastLoginAt', header: 'Last sign-in', minWidth: 130, defaultHidden: true, sortAccessor: (r) => r.lastLoginAt ?? '', exportValue: (r) => (r.lastLoginAt ? formatDateOnly(r.lastLoginAt) : ''), render: (r) => (r.lastLoginAt ? formatDateOnly(r.lastLoginAt) : '—') },
    ],
    [isPlatform],
  )

  const coPag = companiesQuery.data?.pagination
  const empPag = employeesQuery.data?.pagination

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]">
          <BarChart3 size={20} />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-[color:var(--color-ink-strong)] m-0">Analytics</h1>
          <p className="text-sm text-[color:var(--color-ink-muted)] m-0">
            {isPlatform
              ? 'Employee progress across every company.'
              : 'Task, goal, report and verification progress across your company.'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        {isPlatform ? (
          <div className="flex flex-col gap-1">
            <label htmlFor="an-company" className="text-xs text-[color:var(--color-ink-muted)]">Company</label>
            <select
              id="an-company"
              value={company}
              onChange={(e) => { setCompany(e.target.value); setDepartment(''); setEmpPage(1); setCoPage(1) }}
              className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink-strong)] min-w-[220px]"
            >
              <option value="">All companies</option>
              {companyOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        ) : null}
        {departments.length > 0 ? (
          <div className="flex flex-col gap-1">
            <label htmlFor="an-dept" className="text-xs text-[color:var(--color-ink-muted)]">Department</label>
            <select
              id="an-dept"
              value={department}
              onChange={(e) => { setDepartment(e.target.value); setEmpPage(1) }}
              className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink-strong)] min-w-[180px]"
            >
              <option value="">All departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="flex flex-col gap-1">
          <label htmlFor="an-period" className="text-xs text-[color:var(--color-ink-muted)]">Period</label>
          <select
            id="an-period"
            value={period}
            onChange={(e) => { setPeriod(e.target.value); applyPeriodIfValid({ period: e.target.value, customFrom, customTo }) }}
            className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink-strong)] min-w-[160px]"
          >
            <option value="all">All time</option>
            <option value="today">Today (created or running)</option>
            <option value="week">This week</option>
            <option value="month">This month</option>
            <option value="custom">Custom range</option>
          </select>
        </div>
        {period === 'custom' ? (
          <>
            <div className="flex flex-col gap-1">
              <label htmlFor="an-from" className="text-xs text-[color:var(--color-ink-muted)]">From</label>
              <input id="an-from" type="date" value={customFrom} onChange={(e) => { setCustomFrom(e.target.value); applyPeriodIfValid({ period, customFrom: e.target.value, customTo }) }} className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink-strong)]" />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="an-to" className="text-xs text-[color:var(--color-ink-muted)]">To</label>
              <input id="an-to" type="date" value={customTo} onChange={(e) => { setCustomTo(e.target.value); applyPeriodIfValid({ period, customFrom, customTo: e.target.value }) }} className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink-strong)]" />
            </div>
            {/* [QA-15] Say why the range isn't applied yet (the previous period stays). */}
            {draftRange.status !== 'ok' ? (
              <p role="status" className="self-end pb-2 text-xs text-[color:var(--color-warning)]">
                {draftRange.status === 'invalid'
                  ? 'The From date must be on or before the To date.'
                  : 'Choose both dates to apply a custom range.'}{' '}
                Showing {periodLabel}.
              </p>
            ) : null}
          </>
        ) : null}
      </div>

      {/* KPI strip */}
      {summaryQuery.isError ? (
        <div className="rounded-xl border border-[color:var(--color-danger-border)] bg-[color:var(--color-danger-bg)] p-4 text-sm">
          {extractApiError(summaryQuery.error).message}
          <button type="button" onClick={() => summaryQuery.refetch()} className="ml-3 underline">Retry</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {isPlatform ? (
            <KpiCard label="Companies" value={kpis.companies} icon={Building2} tone="violet" />
          ) : null}
          <KpiCard label="Employees" value={kpis.employees} icon={Users} tone="sky" />
          <KpiCard label="Tasks" value={kpis.tasksTotal} icon={ClipboardList} tone="violet" />
          <KpiCard label="Repeat tasks" value={kpis.repeatedTotal} icon={Repeat} tone="sky" hint="Recurring occurrences" />
          <KpiCard label="Non-repeat tasks" value={kpis.normalTotal} icon={ClipboardList} tone="slate" hint="One-off tasks" />
          <KpiCard label="Complete" value={kpis.tasksCompleted} icon={CheckCircle2} tone="emerald" hint={`${periodLabel} · ${kpis.completionRate ?? 0}% overall`} />
          <KpiCard label="To Do" value={kpis.tasksTodo} icon={ClipboardList} tone="slate" />
          <KpiCard label="In Progress" value={kpis.tasksInProgress} icon={Loader} tone="violet" />
          <KpiCard label="Overdue" value={kpis.tasksOverdue} icon={AlertTriangle} tone="rose" />
          <KpiCard label="Goals completed" value={kpis.goalsCompleted} icon={Target} tone="amber" hint={`${kpis.goalsActive ?? 0} active`} />
          <KpiCard label="Reports submitted" value={kpis.reportsSubmitted} icon={FileText} tone="sky" hint={periodLabel} />
          <KpiCard label="Verification pending" value={kpis.verificationPending} icon={ShieldCheck} tone="amber" />
        </div>
      )}

      {/* Task-status breakdown */}
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
        <h3 className="text-sm font-semibold text-[color:var(--color-ink-strong)] m-0 mb-4">Task status</h3>
        {donutTotal > 0 ? (
          <div className="flex flex-wrap items-center gap-8">
            <DonutChart segments={donutSegments} centerLabel="Tasks" centerValue={donutTotal} />
            <Legend segments={donutSegments} />
          </div>
        ) : (
          <p className="text-sm text-[color:var(--color-ink-muted)]">No tasks in scope yet.</p>
        )}
      </div>

      {/* Company-wise comparison (platform only; hidden when drilled into a department) */}
      {isPlatform && !department ? (
        <div>
          <h3 className="text-sm font-semibold text-[color:var(--color-ink-strong)] mb-2">Company comparison</h3>
          <DataTable
            tableId="platform-analytics-companies"
            columns={companyColumns}
            rows={companiesQuery.data?.data ?? []}
            getRowId={(r) => r.id}
            loading={companiesQuery.isLoading}
            fetching={companiesQuery.isFetching}
            error={companiesQuery.isError ? extractApiError(companiesQuery.error).message : null}
            onRetry={() => companiesQuery.refetch()}
            search={coSearch}
            onSearchChange={(v) => { setCoSearch(v); setCoPage(1) }}
            searchPlaceholder="Search company"
            page={coPag?.page ?? coPage}
            totalPages={coPag?.totalPages ?? 1}
            total={coPag?.total ?? 0}
            limit={coPag?.limit ?? coLimit}
            onPageChange={setCoPage}
            onLimitChange={(n) => { setCoLimit(n); setCoPage(1) }}
            limitOptions={analyticsLimitOptions}
            exportBaseName="company-analytics"
            exportTitle="Company analytics"
            getExportRows={() => fetchAllPages('/super-admin/analytics/companies', companyParams, (d) => analyticsCompaniesResponse.parse(d))}
            emptyState={{ icon: Building2, title: 'No companies in scope' }}
          />
        </div>
      ) : null}

      {/* Employee-wise comparison */}
      <div>
        <h3 className="text-sm font-semibold text-[color:var(--color-ink-strong)] mb-2">Employee progress</h3>
        <DataTable
          tableId="analytics-employees"
          columns={employeeColumns}
          rows={employeesQuery.data?.data ?? []}
          getRowId={(r) => r.id}
          loading={employeesQuery.isLoading}
          fetching={employeesQuery.isFetching}
          error={employeesQuery.isError ? extractApiError(employeesQuery.error).message : null}
          onRetry={() => employeesQuery.refetch()}
          search={empSearch}
          onSearchChange={(v) => { setEmpSearch(v); setEmpPage(1) }}
          searchPlaceholder="Search name or email"
          page={empPag?.page ?? empPage}
          totalPages={empPag?.totalPages ?? 1}
          total={empPag?.total ?? 0}
          limit={empPag?.limit ?? empLimit}
          onPageChange={setEmpPage}
          onLimitChange={(n) => { setEmpLimit(n); setEmpPage(1) }}
          limitOptions={analyticsLimitOptions}
          exportBaseName="employee-analytics"
          exportTitle="Employee analytics"
          getExportRows={() => fetchAllPages(`${base}/employees`, employeeParams, (d) => analyticsEmployeesResponse.parse(d))}
          emptyState={{ icon: Users, title: 'No employees in scope' }}
        />
      </div>
    </div>
  )
}
